import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';

import { CompositeFormDirective } from '../../../sdk/forms/composite-form';
import { InputMaskDirective } from '../../../sdk/forms/mask';

@Component({
  selector: 'rms-number-input-stepper',
  imports: [
    MatInputModule,
    MatIconModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatTooltipModule,
    InputMaskDirective,
    MatButton,
  ],
  template: `
    <div class="flex items-center justify-start w-full gap-2">
      <button
        color="primary"
        mat-raised-button
        class="compact-button square-button"
        [disabled]="form.disabled || isDecrementDisabled"
        (click)="decrement()"
        type="button">
        <mat-icon svgIcon="remix:System/subtract-line" />
      </button>

      <mat-form-field
        [class]="'form-sm ' + inputClass"
        subscriptSizing="dynamic">
        <input
          matInput
          class="text-center"
          [formControl]="form"
          rmsInputMask
          [mask]="numberInputMask"
          [unmask]="numberInputUnmask"
          [placeholder]="placeholder" />
      </mat-form-field>

      <div
        [matTooltip]="incrementTooltip"
        [matTooltipDisabled]="!isIncrementDisabled || form.disabled || !incrementTooltip"
        class="inline-block">
        <button
          color="primary"
          mat-raised-button
          class="compact-button square-button"
          [disabled]="form.disabled || isIncrementDisabled"
          (click)="increment()"
          type="button">
          <mat-icon svgIcon="remix:System/add-line" />
        </button>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberInputStepperComponent extends CompositeFormDirective<number> implements OnChanges {
  @Input() public min = 1;
  @Input() public max = 10;
  @Input() public step = 1;
  @Input() public placeholder = '';
  @Input() public incrementTooltip = '';
  @Input() public inputClass = 'w-16';

  private readonly cdr = inject(ChangeDetectorRef);

  protected form = this.fb.control(this.min);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['min'] || changes['max']) {
      const value = this.form.value ?? 0;
      if (value < this.min) {
        this.form.patchValue(this.min);
      } else if (value > this.max) {
        this.form.patchValue(this.max);
      }

      this.cdr.markForCheck();
    }
  }

  public get isDecrementDisabled(): boolean {
    if (this.form.disabled) {
      return true;
    }

    const currentValue = this.form.value ?? this.min;

    return currentValue <= this.min;
  }

  public get isIncrementDisabled(): boolean {
    if (this.form.disabled) {
      return true;
    }

    const currentValue = this.form.value ?? this.min;

    return currentValue >= this.max;
  }

  public increment(): void {
    const currentValue = this.form.value ?? this.min;
    const newValue = Math.min(currentValue + this.step, this.max);

    this.form.patchValue(newValue);
  }

  public decrement(): void {
    const currentValue = this.form.value ?? this.min;
    const newValue = Math.max(currentValue - this.step, this.min);

    this.form.patchValue(newValue);
  }

  protected numberInputMask = (input: string | number | null): string => {
    const value = typeof input === 'number' ? input.toString() : input;

    if (Number(value) > this.max) {
      return this.max.toString();
    }

    if (Number(value) < this.min) {
      return this.min.toString();
    }

    return (value ?? '').replace(/\D/g, '');
  };

  protected numberInputUnmask = (value: string): number | null => (!value.length ? null : Number(value));
}
